import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import { injectIntl, FormattedMessage } from 'react-intl';
import moment from 'moment';

import { userPointsStatement as userPointsStatementShape, customerInfo as customerInfoShape } from 'constants/propTypes';
import Icon from 'components/ui/Icons';
import FormattedCurrency from 'components/ui/FormattedCurrency';

import {
  BookingLink,
  StyledTableRow,
  StyledTableHeader,
  StyledTableCell,
  StyledDescriptionList,
  StyledDescriptionTerm,
  StyledDescriptionDetails,
  StyledRowExpander,
  AdditionalNotesIcon
} from '../ClubEurostarMember.styles';
import { TooltipContainer } from '../MemberInfo/MemberInfo.styles';

import ClaimPointsFilled from 'assets/rebrand/icons/claimPointsFilled.svg';
import Information from 'assets/rebrand/icons/information.svg';
import { StyledPointsValue } from './PointsStatement.styles';
import { LOYALTY_NUMBER_PREFIX } from '../../../constants/passengers';

const DescriptionCell = ({ description, index }) => {
  const loyaltyNumberPrefix = LOYALTY_NUMBER_PREFIX.toString();
  if (description.includes(loyaltyNumberPrefix)) {
    const startIndex = description.indexOf(loyaltyNumberPrefix);
    const membershipNumber = description.slice(startIndex, startIndex + 17);
    const descriptionArray = description.split(membershipNumber);
    return (
      <span>
        {`${descriptionArray[0]} `}
        <a href={`/clubeurostar/${membershipNumber}`} target="_blank" rel="noreferrer" data-testid={`membershipNumber-link-${index}`}>
          {membershipNumber}
        </a>
        {` ${descriptionArray[1]}`}
      </span>
    );
  } else {
    return <span>{description}</span>;
  }
};

DescriptionCell.propTypes = {
  description: PropTypes.string,
  index: PropTypes.number
};
const PointsStatementTableRow = ({ formatMessage, membershipNumber, statement, index }) => {
  const {
    actionBy,
    bookingReference,
    createdAt,
    description,
    acceleratorPoints: rawAcceleratorPoints,
    rewardPoints: rawRewardPoints,
    rewardPointsBalance,
    statusPoints: rawStatusPoints,
    statusPointsBalance,
    additionalNotes,
    journalSubTypeName,
    journalTypeName,
    lastModifiedByName,
    ticket,
    currency,
    transactionAmount
  } = statement;

  const shouldShowEarnedPoints = journalSubTypeName === 'Purchase a Ticket' && journalTypeName === 'Accrual';
  const shouldShowAdjustmentBreakdown = ['Credit', 'Debit'].includes(journalSubTypeName) && journalTypeName === 'Manual Points Adjustment';
  const shouldShowAdditionalDetails = Boolean(actionBy || lastModifiedByName);
  const expandable = shouldShowEarnedPoints || shouldShowAdditionalDetails;

  let acceleratorPoints = rawAcceleratorPoints;
  let rewardPoints = rawRewardPoints;
  let statusPoints = rawStatusPoints;

  if (expandable) {
    acceleratorPoints = acceleratorPoints ? acceleratorPoints : 0;
    rewardPoints = rewardPoints ? rewardPoints : 0;
    statusPoints = statusPoints ? statusPoints : 0;
  }

  let basePoints = statusPoints;

  const [isExpanded, setIsExpanded] = useState(false);

  if (shouldShowEarnedPoints) {
    acceleratorPoints = rewardPoints - statusPoints;
    basePoints = statusPoints;
  } else if (shouldShowAdjustmentBreakdown) {
    basePoints = rewardPoints - acceleratorPoints;
  }

  // Percentage if accelerator points > 0= ({accelerator points} * 100 ) % {statusPoints}. If not, 0%.
  const acceleratorPointsPercentageBase = acceleratorPoints > 0 ? Math.round((acceleratorPoints * 100) / basePoints) : 0;
  // Rounding this to the nearest 25%
  const acceleratorPointsPercentage = Math.round(acceleratorPointsPercentageBase / 25) * 25;

  return (
    <>
      <StyledTableRow data-cy="user-points-statement" data-testid={`user-points-statement-row-${index}`} columns={8}>
        <StyledTableCell span={2} justifyContent="center">
          {expandable && (
            <StyledRowExpander
              onClick={() => setIsExpanded(!isExpanded)}
              expanded={isExpanded}
              data-testid={`user-points-statement-row-expander-button-${index}`}
            >
              <Icon icon="ChevronRight" />
            </StyledRowExpander>
          )}
          {moment(createdAt).format('DD-MMM-YYYY')}
        </StyledTableCell>
        <StyledTableCell span={1} justifyContent="center">
          <BookingLink href={`/manage/bookings/${bookingReference}/journeys?membershipNumber=${membershipNumber}`} target="_blank">
            {bookingReference}
          </BookingLink>
        </StyledTableCell>
        <StyledTableCell span={1} justifyContent="center">
          {rewardPoints !== null && (
            <>
              <StyledPointsValue
                data-for={`statement_reward_balance_${index}`}
                data-tip
                onClick={() => setIsExpanded(!isExpanded)}
                data-testid={`statement-reward-balance-value_${index}`}
              >
                {rewardPoints > 0 ? '+' : rewardPoints <= 0 ? '' : '-'}
                {rewardPoints}
                <AdditionalNotesIcon src={ClaimPointsFilled} />
              </StyledPointsValue>
              <ReactTooltip
                className="tooltip"
                id={`statement_reward_balance_${index}`}
                data-testid={`point-statement-reward-balance-tooltip_${index}`}
                place="bottom"
              >
                <TooltipContainer>
                  {formatMessage({ id: 'clubEurostar.balance' })}: {rewardPointsBalance}
                </TooltipContainer>
              </ReactTooltip>
            </>
          )}
        </StyledTableCell>
        <StyledTableCell span={1} justifyContent="center">
          {statusPoints !== null && (
            <>
              <StyledPointsValue
                data-for={`statement_status_balance_${index}`}
                data-tip
                onClick={() => setIsExpanded(!isExpanded)}
                data-testid={`statement-status-balance-value_${index}`}
              >
                {statusPoints > 0 ? '+' : statusPoints <= 0 ? '' : '-'}
                {statusPoints}
                <AdditionalNotesIcon src={ClaimPointsFilled} />
              </StyledPointsValue>
              <ReactTooltip
                className="tooltip"
                id={`statement_status_balance_${index}`}
                data-testid={`point-statement-status-balance-tooltip_${index}`}
                place="bottom"
              >
                <TooltipContainer>
                  {formatMessage({ id: 'clubEurostar.balance' })}: {statusPointsBalance}
                </TooltipContainer>
              </ReactTooltip>
            </>
          )}
        </StyledTableCell>
        <StyledTableCell span={3} justifyContent="center">
          <DescriptionCell description={description} index={index} />
          {additionalNotes && (
            <>
              <AdditionalNotesIcon src={Information} data-for={`statement_item_${index}`} data-tip />
              <ReactTooltip
                className="tooltip"
                id={`statement_item_${index}`}
                data-cy="point-statement-additional-note-tooltip"
                place="bottom"
              >
                <TooltipContainer>{additionalNotes}</TooltipContainer>
              </ReactTooltip>
            </>
          )}
        </StyledTableCell>
      </StyledTableRow>
      {expandable && (
        <StyledTableRow
          columns={8}
          expandable={expandable}
          expanded={isExpanded}
          data-testid={`user-points-statement-row-expandable-${index}`}
        >
          <StyledTableCell span={8} justifyContent="center">
            <StyledDescriptionList>
              {shouldShowEarnedPoints || shouldShowAdjustmentBreakdown ? (
                <>
                  <div>
                    <StyledDescriptionTerm>
                      <FormattedMessage id="clubEurostar.ticket" />
                    </StyledDescriptionTerm>
                    <StyledDescriptionDetails>{ticket}</StyledDescriptionDetails>
                  </div>

                  {shouldShowEarnedPoints && (
                    <div>
                      <StyledDescriptionTerm>
                        <FormattedMessage id="clubEurostar.price" />
                      </StyledDescriptionTerm>
                      <StyledDescriptionDetails>
                        <FormattedCurrency currency={currency} amount={transactionAmount} />
                      </StyledDescriptionDetails>
                    </div>
                  )}

                  <div>
                    <StyledDescriptionTerm>
                      <FormattedMessage id="clubEurostar.basePoints" />
                    </StyledDescriptionTerm>
                    <StyledDescriptionDetails>
                      {basePoints > 0 ? '+' : basePoints <= 0 ? '' : '-'}
                      {basePoints}
                    </StyledDescriptionDetails>
                  </div>

                  <div>
                    <StyledDescriptionTerm>
                      <FormattedMessage id="clubEurostar.acceleratorPoints" /> ({acceleratorPointsPercentage}%)
                    </StyledDescriptionTerm>
                    <StyledDescriptionDetails>
                      {acceleratorPoints > 0 ? '+' : acceleratorPoints <= 0 ? '' : '-'}
                      {acceleratorPoints}
                    </StyledDescriptionDetails>
                  </div>

                  <div>
                    <StyledDescriptionTerm>
                      <FormattedMessage id="clubEurostar.totalRewardPoints" />
                    </StyledDescriptionTerm>
                    <StyledDescriptionDetails>
                      {rewardPoints > 0 ? '+' : rewardPoints <= 0 ? '' : '-'}
                      {rewardPoints}
                    </StyledDescriptionDetails>
                  </div>
                </>
              ) : null}
              {shouldShowAdditionalDetails ? (
                <div>
                  <StyledDescriptionTerm>
                    <FormattedMessage id="clubEurostar.userName" />
                  </StyledDescriptionTerm>
                  <StyledDescriptionDetails>{actionBy || lastModifiedByName}</StyledDescriptionDetails>
                </div>
              ) : null}
            </StyledDescriptionList>
          </StyledTableCell>
        </StyledTableRow>
      )}
    </>
  );
};

PointsStatementTableRow.propTypes = {
  formatMessage: PropTypes.func.isRequired,
  index: PropTypes.number,
  membershipNumber: PropTypes.string,
  statement: userPointsStatementShape
};

const PointsStatementTable = ({ pointsStatement, customerInfo, intl: { formatMessage } }) => {
  const membershipNumber = customerInfo?.profile?.loyalty?.membershipNumber;
  return (
    <div>
      <StyledTableRow columns={8}>
        <StyledTableHeader span={2} justifyContent="center">
          <FormattedMessage id="extras.insurance.date" tagName="strong" />
        </StyledTableHeader>
        <StyledTableHeader span={1} justifyContent="center">
          <FormattedMessage id="bookingDetails.pnr" tagName="strong" />
        </StyledTableHeader>
        <StyledTableHeader span={1} justifyContent="center">
          <FormattedMessage id="clubEurostar.points" tagName="strong" />
        </StyledTableHeader>
        <StyledTableHeader span={1} justifyContent="center">
          <FormattedMessage id="clubEurostar.statusPoints" tagName="strong" />
        </StyledTableHeader>
        <StyledTableHeader span={3} justifyContent="center">
          <FormattedMessage id="extras.insurance.description" tagName="strong" />
        </StyledTableHeader>
      </StyledTableRow>
      {pointsStatement.map((statement, index) => (
        <PointsStatementTableRow
          formatMessage={formatMessage}
          index={index}
          membershipNumber={membershipNumber}
          statement={statement}
          key={index}
        />
      ))}
    </div>
  );
};

PointsStatementTable.propTypes = {
  pointsStatement: PropTypes.arrayOf(userPointsStatementShape),
  customerInfo: customerInfoShape,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  }).isRequired
};

export default injectIntl(PointsStatementTable);
